<script setup lang="ts">
import type { Layer } from "@/types/legend.types";
import { computed, ref } from "vue";
import LayerConfigItem from "./LayerConfigItem.vue";
import DragReorderList from "./DragReorderList.vue";
import type { InputInstance } from "element-plus";
import DRBtn from "@/components/UI/DRBtn.vue";
import { defaultLayer } from "./edit-legend.models";
import { useTranslation } from "i18next-vue";
import { v4 } from "uuid";

const { t } = useTranslation();

defineProps<{
  layers: Layer[];
}>();

const emits = defineEmits<{
  (e: "create", value: { id: string; name: string }): void;
  (e: "remove", index: number): void;
  (e: "update", payload: { index: number; layer: Layer }): void;
  (e: "reorder", layers: Layer[]): void;
}>();

const newLayerRef = ref<InputInstance | null>(null);
const newLayerName = ref("");

const addLayer = () => {
  emits("create", {
    id: v4(),
    name: newLayerName.value?.length
      ? newLayerName.value.trim()
      : t(defaultLayer.name),
  });
  newLayerName.value = "";
  newLayerRef.value?.focus();
};

const updateLayer = (index: number, value: Layer) => {
  emits("update", { index, layer: value });
};

const removeLayer = (index: number) => {
  emits("remove", index);
};

const isValid = computed(() => {
  return newLayerName.value?.trim().length === 0;
});
</script>

<template>
  <DragReorderList
    v-if="layers.length"
    :items="layers"
    group="legend-layers"
    @reorder="$emit('reorder', $event)"
  >
    <template #default="{ item: layer, index }">
      <LayerConfigItem
        :model-value="layer"
        @update:modelValue="updateLayer(index, $event)"
        @remove="removeLayer(index)"
      />
    </template>
  </DragReorderList>

  <div class="d-flex gap-3 pt-3">
    <el-input
      ref="newLayerRef"
      class="col-3"
      size="small"
      v-model="newLayerName"
      :placeholder="$t(defaultLayer.name)"
      @keyup.enter="addLayer()"
    />
    <DRBtn
      class="col-auto"
      :label="$t('add')"
      variant="secondary"
      :disabled="isValid"
      @click="addLayer()"
    />
  </div>
</template>

<style lang="scss" scoped></style>
